import React from "react";
import { Check } from "lucide-react";
import { WorkspaceCompany } from "@/types/workspace";
import DomainFavicon from "@/components/ui/DomainFavicon";

interface CompanyRowProps {
  company: WorkspaceCompany;
  active: boolean;
  onClick: (company: WorkspaceCompany) => void;
}

const getDisplayHost = (companyUrl?: string | null): string => {
  const trimmedUrl = companyUrl?.trim();
  if (!trimmedUrl) {
    return "No URL";
  }

  try {
    const normalizedUrl = /^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(trimmedUrl)
      ? trimmedUrl
      : `https://${trimmedUrl}`;
    return new URL(normalizedUrl).hostname || "No URL";
  } catch {
    return trimmedUrl
      .replace(/^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//, "")
      .split("/")[0] || "No URL";
  }
};

const CompanyRow: React.FC<CompanyRowProps> = ({ company, active, onClick }) => {
  const metaUrl = getDisplayHost(company.company_url);
  // Only surface the report status when it's a real, in-flight state
  // (in_progress / finalizing / error). A finished ("completed") or absent
  // status carries no signal, so the row shows just the domain rather than a
  // noisy "Unknown".
  const rawStatus = (company.verification_status ?? company.status ?? "").trim();
  const showStatus = rawStatus !== "" && rawStatus.toLowerCase() !== "completed";
  const metaText = [metaUrl, showStatus ? rawStatus.replace(/_/g, " ") : null]
    .filter(Boolean)
    .join(" · ");

  return (
    <button
      type="button"
      onClick={() => onClick(company)}
      className={[
        "w-full rounded-md px-2 py-2 text-left transition hover:bg-[var(--vb-bg-subtle)]",
        active ? "bg-[var(--vb-brand-soft)]" : "",
      ].join(" ")}
    >
      <div className="flex items-center gap-2">
        <DomainFavicon
          name={company.company_name}
          url={company.company_url}
          size={28}
          className="h-7 w-7 self-center"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-[color:var(--vb-text)]">{company.company_name}</p>
          <p className="text-xs leading-4 text-[color:var(--vb-text-3)] whitespace-normal break-words">
            {metaText}
          </p>
        </div>
        {active ? <Check className="h-4 w-4 shrink-0 self-center text-accent" /> : null}
      </div>
    </button>
  );
};

export default CompanyRow;
